import { useState } from "react";
import { connect } from "react-redux";
import { Field, Formik, Form } from "formik";
import { Col, FormGroup, Row, Spinner } from "reactstrap";
import { RenderSingleSelect, RenderTextField } from "../../../../components";
import { IbanElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { addExternalAccount } from "../../actions";

const BANK_ACCOUNT_TYPE_OPTIONS = [
  { value: "checking", label: "Checking" },
  { value: "savings", label: "Savings" },
];

const InternationalAccountForm = ({
  isReplaceAccountForm,

  loginUser,
  loading,

  addExternalAccount,
  ...props
}) => {
  const [ibanSubmitError, setIbanSubmitError] = useState(null);
  const [aedAccountSubmitError, setAedAccountSubmitError] = useState(null);
  const [cadAccountSubmitError, setCadAccountSubmitError] = useState(null);
  const [gbpAccountSubmitError, setGbpAccountSubmitError] = useState(null);
  const [audAccountSubmitError, setAudAccountSubmitError] = useState(null);
  const [hkdAccountSubmitError, setHkdAccountSubmitError] = useState(null);
  const [ilsAccountSubmitError, setIlsAccountSubmitError] = useState(null);
  const [nzdAccountSubmitError, setNzdAccountSubmitError] = useState(null);
  const [mxnAccountSubmitError, setMxnAccountSubmitError] = useState(null);
  const [zarAccountSubmitError, setZarAccountSubmitError] = useState(null);
  const [inrAccountSubmitError, setInrAccountSubmitError] = useState(null);
  const [thbAccountSubmitError, setThbAccountSubmitError] = useState(null);
  const [phpAccountSubmitError, setPhpAccountSubmitError] = useState(null);
  const [ngnAccountSubmitError, setNgnAccountSubmitError] = useState(null);
  const [arsAccountSubmitError, setArsAccountSubmitError] = useState(null);
  const [clpAccountSubmitError, setClpAccountSubmitError] = useState(null);
  const [gtqAccountSubmitError, setGtqAccountSubmitError] = useState(null);
  const [madAccountSubmitError, setMadAccountSubmitError] = useState(null);
  const [jpyAccountSubmitError, setJpyAccountSubmitError] = useState(null);
  const [kztAccountSubmitError, setKztAccountSubmitError] = useState(null);
  const [twdAccountSubmitError, setTwdAccountSubmitError] = useState(null);

  const stripe = useStripe();
  const elements = useElements();

  const handleExternalAccountSubmit = async(currency = "eur") => {
    setIbanSubmitError(null);
    if (!elements) {
      return;
    }
    const ibanElement = elements.getElement(IbanElement);
    if (!ibanElement) {
      return;
    }

    stripe.createToken(ibanElement, { currency }).then(async(result) => {
      if (result.error) {
        if (result.error.type === "validation_error") {
          setIbanSubmitError(result.error.message);
        } else {
          setIbanSubmitError("IBAN is invalid");
        }
      } else {
        setIbanSubmitError(null);
        addExternalAccount(result?.token?.id);
      }
    });
  };

  const handleCAExternalAccountSubmit = async({
    transit_number,
    institution_number,
    ...values
  }) => {
    values.routing_number = `${transit_number?.trim()}-${institution_number?.trim()}`;
    stripe
      .createToken("bank_account", {
        ...values,
        currency: "cad",
        country: "CA",
      })
      .then(async(result) => {
        if (result.error) {
          if (result.error.type === "validation_error") {
            setCadAccountSubmitError(result.error.message);
          } else {
            setCadAccountSubmitError("Account is invalid");
          }
        } else {
          setCadAccountSubmitError(null);
          addExternalAccount(result?.token?.id);
        }
      });
  };

  const handleAUExternalAccountSubmit = async({ bsb_code, ...values }) => {
    values.routing_number = bsb_code;
    stripe
      .createToken("bank_account", {
        ...values,
        currency: "aud",
        country: "AU",
      })
      .then(async(result) => {
        if (result.error) {
          if (result.error.type === "validation_error") {
            setAudAccountSubmitError(result.error.message);
          } else {
            setAudAccountSubmitError("Account is invalid");
          }
        } else {
          setAudAccountSubmitError(null);
          addExternalAccount(result?.token?.id);
        }
      });
  };
  const handleHKExternalAccountSubmit = async({
    clearing_code,
    branch_code,
    ...values
  }) => {
    values.routing_number = `${clearing_code?.trim()}-${branch_code?.trim()}`;
    stripe
      .createToken("bank_account", {
        ...values,
        currency: "hkd",
        country: "HK",
      })
      .then(async(result) => {
        if (result.error) {
          if (result.error.type === "validation_error") {
            setHkdAccountSubmitError(result.error.message);
          } else {
            setHkdAccountSubmitError("Account is invalid");
          }
        } else {
          setHkdAccountSubmitError(null);
          addExternalAccount(result?.token?.id);
        }
      });
  };

  const handleILExternalAccountSubmit = async(values) => {
    if (values.routing_number) {
      delete values.routing_number;
    }
    stripe
      .createToken("bank_account", {
        ...values,
        currency: "ils",
        country: "IL",
      })
      .then(async(result) => {
        if (result.error) {
          if (result.error.type === "validation_error") {
            setIlsAccountSubmitError(result.error.message);
          } else {
            setIlsAccountSubmitError("Account is invalid");
          }
        } else {
          setIlsAccountSubmitError(null);
          addExternalAccount(result?.token?.id);
        }
      });
  };

  const handleAEExternalAccountSubmit = async(values) => {
    if (values.routing_number) {
      delete values.routing_number;
    }
    stripe
      .createToken("bank_account", {
        ...values,
        currency: "aed",
        country: "AE",
      })
      .then(async(result) => {
        if (result.error) {
          if (result.error.type === "validation_error") {
            setAedAccountSubmitError(result.error.message);
          } else {
            setAedAccountSubmitError("Account is invalid");
          }
        } else {
          setAedAccountSubmitError(null);
          addExternalAccount(result?.token?.id);
        }
      });
  };

  const handleGBExternalAccountSubmit = async(values) => {
    if (values.sort_code) {
      values.routing_number = values?.sort_code;
    } else {
      delete values.routing_number;
    }
    stripe
      .createToken("bank_account", {
        ...values,
        currency: "gbp",
        country: "GB",
      })
      .then(async(result) => {
        if (result.error) {
          if (result.error.type === "validation_error") {
            setGbpAccountSubmitError(result.error.message);
          } else {
            setGbpAccountSubmitError("Account is invalid");
          }
        } else {
          setGbpAccountSubmitError(null);
          addExternalAccount(result?.token?.id);
        }
      });
  };

  const handleNZExternalAccountSubmit = async(values) => {
    if (values.routing_number) {
      delete values.routing_number;
    }
    stripe
      .createToken("bank_account", {
        ...values,
        currency: "nzd",
        country: "NZ",
      })
      .then(async(result) => {
        if (result.error) {
          if (result.error.type === "validation_error") {
            setNzdAccountSubmitError(result.error.message);
          } else {
            setNzdAccountSubmitError("Account is invalid");
          }
        } else {
          setNzdAccountSubmitError(null);
          addExternalAccount(result?.token?.id);
        }
      });
  };

  const handleMXExternalAccountSubmit = async(values) => {
    if (values.routing_number) {
      delete values.routing_number;
    }
    stripe
      .createToken("bank_account", {
        ...values,
        currency: "mxn",
        country: "MX",
      })
      .then(async(result) => {
        if (result.error) {
          if (result.error.type === "validation_error") {
            setMxnAccountSubmitError(result.error.message);
          } else {
            setMxnAccountSubmitError("Account is invalid");
          }
        } else {
          setMxnAccountSubmitError(null);
          addExternalAccount(result?.token?.id);
        }
      });
  };
  const handleZAExternalAccountSubmit = async(values) => {
    if (values.swift_bic) {
      values.routing_number = values?.swift_bic;
    } else {
      delete values.routing_number;
    }
    stripe
      .createToken("bank_account", {
        ...values,
        currency: "zar",
        country: "ZA",
      })
      .then(async(result) => {
        if (result.error) {
          if (result.error.type === "validation_error") {
            setZarAccountSubmitError(result.error.message);
          } else {
            setZarAccountSubmitError("Account is invalid");
          }
        } else {
          setZarAccountSubmitError(null);
          addExternalAccount(result?.token?.id);
        }
      });
  };
  const handleINExternalAccountSubmit = async(values) => {
    if (values.ifsc_code) {
      values.routing_number = values?.ifsc_code;
    } else {
      delete values.routing_number;
    }
    stripe
      .createToken("bank_account", {
        ...values,
        currency: "inr",
        country: "IN",
      })
      .then(async(result) => {
        if (result.error) {
          if (result.error.type === "validation_error") {
            setInrAccountSubmitError(result.error.message);
          } else {
            setInrAccountSubmitError("Account is invalid");
          }
        } else {
          setInrAccountSubmitError(null);
          addExternalAccount(result?.token?.id);
        }
      });
  };
  const handleTHExternalAccountSubmit = async(values) => {
    if (values.bank_code) {
      values.routing_number = values?.bank_code;
    } else {
      delete values.routing_number;
    }
    stripe
      .createToken("bank_account", {
        ...values,
        currency: "thb",
        country: "TH",
      })
      .then(async(result) => {
        if (result.error) {
          if (result.error.type === "validation_error") {
            setThbAccountSubmitError(result.error.message);
          } else {
            setThbAccountSubmitError("Account is invalid");
          }
        } else {
          setThbAccountSubmitError(null);
          addExternalAccount(result?.token?.id);
        }
      });
  };
  const handlePHExternalAccountSubmit = async(values) => {
    if (values.swift_bic) {
      values.routing_number = values?.swift_bic;
    } else {
      delete values.routing_number;
    }
    stripe
      .createToken("bank_account", {
        ...values,
        currency: "php",
        country: "PH",
      })
      .then(async(result) => {
        if (result.error) {
          if (result.error.type === "validation_error") {
            setPhpAccountSubmitError(result.error.message);
          } else {
            setPhpAccountSubmitError("Account is invalid");
          }
        } else {
          setPhpAccountSubmitError(null);
          addExternalAccount(result?.token?.id);
        }
      });
  };
  const handleNGExternalAccountSubmit = async(values) => {
    if (values.swift_bic) {
      values.routing_number = values?.swift_bic;
    } else {
      delete values.routing_number;
    }
    stripe
      .createToken("bank_account", {
        ...values,
        currency: "ngn",
        country: "NG",
      })
      .then(async(result) => {
        if (result.error) {
          if (result.error.type === "validation_error") {
            setNgnAccountSubmitError(result.error.message);
          } else {
            setNgnAccountSubmitError("Account is invalid");
          }
        } else {
          setNgnAccountSubmitError(null);
          addExternalAccount(result?.token?.id);
        }
      });
  };
  const handleGTExternalAccountSubmit = async(values) => {
    if (values.swift_bic) {
      values.routing_number = values?.swift_bic;
    } else {
      delete values.routing_number;
    }
    stripe
      .createToken("bank_account", {
        ...values,
        currency: "gtq",
        country: "GT",
      })
      .then(async(result) => {
        if (result.error) {
          if (result.error.type === "validation_error") {
            setGtqAccountSubmitError(result.error.message);
          } else {
            setGtqAccountSubmitError("Account is invalid");
          }
        } else {
          setGtqAccountSubmitError(null);
          addExternalAccount(result?.token?.id);
        }
      });
  };
  const handleCLExternalAccountSubmit = async(values) => {
    if (values.bank_code) {
      values.routing_number = values?.bank_code;
    } else {
      delete values.routing_number;
    }

    // Get bank_account_type directly from form values
    const accountType = values.bank_account_type?.value || "checking";

    stripe
      .createToken("bank_account", {
        ...values,
        account_type: accountType,
        currency: "clp",
        country: "CL",
      })
      .then(async(result) => {
        if (result.error) {
          if (result.error.type === "validation_error") {
            setClpAccountSubmitError(result.error.message);
          } else {
            setClpAccountSubmitError("Account is invalid");
          }
        } else {
          setClpAccountSubmitError(null);
          addExternalAccount(result?.token?.id);
        }
      });
  };
  const handleARExternalAccountSubmit = async(values) => {
    delete values.routing_number;
    stripe
      .createToken("bank_account", {
        ...values,
        currency: "ars",
        country: "AR",
      })
      .then(async(result) => {
        if (result.error) {
          if (result.error.type === "validation_error") {
            setArsAccountSubmitError(result.error.message);
          } else {
            setArsAccountSubmitError("Account is invalid");
          }
        } else {
          setArsAccountSubmitError(null);
          addExternalAccount(result?.token?.id);
        }
      });
  };
  const handleMAExternalAccountSubmit = async(values) => {
    if (values.swift_bic) {
      values.routing_number = values?.swift_bic;
    } else {
      delete values.routing_number;
    }
    stripe
      .createToken("bank_account", {
        ...values,
        currency: "mad",
        country: "MA",
      })
      .then(async(result) => {
        if (result.error) {
          if (result.error.type === "validation_error") {
            setMadAccountSubmitError(result.error.message);
          } else {
            setMadAccountSubmitError("Account is invalid");
          }
        } else {
          setMadAccountSubmitError(null);
          addExternalAccount(result?.token?.id);
        }
      });
  };
  const handleKZExternalAccountSubmit = async(values) => {
    const accountInfo = {
      account_number: values?.account_number,
      routing_number: values?.swift_bic,
      currency: "kzt",
      country: "KZ",
    };

    stripe.createToken("bank_account", accountInfo).then(async(result) => {
      if (result.error) {
        if (result.error.type === "validation_error") {
          setKztAccountSubmitError(result.error.message);
        } else {
          setKztAccountSubmitError("Account is invalid");
        }
      } else {
        setKztAccountSubmitError(null);
        addExternalAccount(result?.token?.id);
      }
    });
  };
  const handleTWExternalAccountSubmit = async(values) => {
    const accountInfo = {
      account_number: values?.account_number,
      routing_number: values?.swift_bic,
      currency: "twd",
      country: "TW",
    };

    stripe.createToken("bank_account", accountInfo).then(async(result) => {
      if (result.error) {
        if (result.error.type === "validation_error") {
          setTwdAccountSubmitError(result.error.message);
        } else {
          setTwdAccountSubmitError("Account is invalid");
        }
      } else {
        setTwdAccountSubmitError(null);
        addExternalAccount(result?.token?.id);
      }
    });
  };
  const handleJPExternalAccountSubmit = async(values) => {
    /*
    Bank name	いろは銀行
    Branch name	東京支店
    Bank code	0123 (4 digits)
    Branch code	456 (3 digits)
    Account number	1234567 (4-8 digits)
    Account owner name	ヤマダハナコ
    */

    const accountInfo = {
      account_number: values.account_number,
      bank_name: values.bank_name,
      // branch_name:values.branch_name,
      account_holder_name: values.account_holder_name,
      currency: "jpy",
      country: "JP",
    };

    accountInfo.routing_number = `${values.bank_code}${values.branch_code}`;

    stripe.createToken("bank_account", accountInfo).then(async(result) => {
      if (result.error) {
        if (result.error.type === "validation_error") {
          setJpyAccountSubmitError(result.error.message);
        } else {
          setJpyAccountSubmitError("Account is invalid");
        }
      } else {
        setJpyAccountSubmitError(null);
        addExternalAccount(result?.token?.id);
      }
    });
  };

  const handleFormSubmit = async(values) => {
    if (loginUser.country === "CA") {
      handleCAExternalAccountSubmit(values);
    } else if (loginUser.country === "GB") {
      handleGBExternalAccountSubmit(values);
    } else if (loginUser.country === "AU") {
      handleAUExternalAccountSubmit(values);
    } else if (loginUser.country === "HK") {
      handleHKExternalAccountSubmit(values);
    } else if (loginUser.country === "NZ") {
      handleNZExternalAccountSubmit(values);
    } else if (loginUser.country === "MX") {
      handleMXExternalAccountSubmit(values);
    } else if (loginUser.country === "IL") {
      handleILExternalAccountSubmit(values);
    } else if (loginUser.country === "AE") {
      handleAEExternalAccountSubmit(values);
    } else if (loginUser.country === "ZA") {
      handleZAExternalAccountSubmit(values);
    } else if (loginUser.country === "IN") {
      handleINExternalAccountSubmit(values);
    } else if (loginUser.country === "TH") {
      handleTHExternalAccountSubmit(values);
    } else if (loginUser.country === "PH") {
      handlePHExternalAccountSubmit(values);
    } else if (loginUser.country === "NG") {
      handleNGExternalAccountSubmit(values);
    } else if (loginUser.country === "CL") {
      handleCLExternalAccountSubmit(values);
    } else if (loginUser.country === "AR") {
      handleARExternalAccountSubmit(values);
    } else if (loginUser.country === "GT") {
      handleGTExternalAccountSubmit(values);
    } else if (loginUser.country === "MA") {
      handleMAExternalAccountSubmit(values);
    } else if (loginUser.country === "JP") {
      handleJPExternalAccountSubmit(values);
    } else if (loginUser.country === "KZ") {
      handleKZExternalAccountSubmit(values);
    } else if (loginUser.country === "TW") {
      handleTWExternalAccountSubmit(values);
    } else {
      handleExternalAccountSubmit();
    }
  };

  const initialValuesCanada = {
    transit_number: "",
    institution_number: "",
    account_number: "",
    sort_code: "",
    bsb_code: "",
    clearing_code: "",
    branch_code: "",
    swift_bic: "",
    bank_name: "",
    bank_code: "",
    ifsc_code: "",
    account_holder_name: "",
    // branch_name: "",
    bank_account_type: { value: "checking", label: "Checking" },
  };

  return (
    <>
      <div className="subheading text-grey">
        <p className="mb-2">
          Enter your bank information below to{" "}
          {isReplaceAccountForm ? "change" : "link"} the{" "}
          {loginUser.country.toUpperCase() || "non-US"} bank account where your
          payouts will be deposited.
        </p>
        {loginUser.country === "CA" && (
          <p>Payouts are currently available in CAD.</p>
        )}
        {loginUser.country === "GB" && (
          <p>Payouts are currently available in GBP.</p>
        )}
        {loginUser.country === "AU" && (
          <p>Payouts are currently available in AUD.</p>
        )}
        {loginUser.country === "AE" && (
          <p>Payouts are currently available in AED.</p>
        )}
        {loginUser.country === "HK" && (
          <p>Payouts are currently available in HKD.</p>
        )}
        {loginUser.country === "IL" && (
          <p>Payouts are currently available in ILS.</p>
        )}
        {loginUser.country === "NZ" && (
          <p>Payouts are currently available in NZD.</p>
        )}
        {loginUser.country === "MX" && (
          <p>Payouts are currently available in MXN.</p>
        )}
        {loginUser.country === "IN" && (
          <p>Payouts are currently available in INR.</p>
        )}
        {loginUser.country === "ZA" && (
          <p>Payouts are currently available in ZAR.</p>
        )}
        {loginUser.country === "TH" && (
          <p>Payouts are currently available in THB.</p>
        )}
        {loginUser.country === "PH" && (
          <p>Payouts are currently available in PHP.</p>
        )}
        {loginUser.country === "NG" && (
          <p>Payouts are currently available in NGN.</p>
        )}
        {loginUser.country === "CL" && (
          <p>Payouts are currently available in CLP.</p>
        )}
        {loginUser.country === "AR" && (
          <p>Payouts are currently available in ARS.</p>
        )}
        {loginUser.country === "GT" && (
          <p>Payouts are currently available in GTQ.</p>
        )}
        {loginUser.country === "MA" && (
          <p>Payouts are currently available in MAD.</p>
        )}
        {loginUser.country === "KZ" && (
          <p>Payouts are currently available in KZT.</p>
        )}
        {loginUser.country === "TW" && (
          <p>Payouts are currently available in TWD.</p>
        )}
        {loginUser.country === "JP" && (
          <>
            <p>
              Only futsū or tōza accounts from banks participating in the
              Foreign Exchange Yen Clearing System (FXYCS) are supported.
            </p>
            <p>Payouts are currently available in JPY.</p>
          </>
        )}
        {loginUser.country !== "NZ"
          && loginUser.country !== "HK"
          && loginUser.country !== "AE"
          && loginUser.country !== "IL"
          && loginUser.country !== "GB"
          && loginUser.country !== "AU"
          && loginUser.country !== "CA"
          && loginUser.country !== "ZA"
          && loginUser.country !== "TH"
          && loginUser.country !== "PH"
          && loginUser.country !== "IN"
          && loginUser.country !== "NG"
          && loginUser.country !== "AR"
          && loginUser.country !== "GT"
          && loginUser.country !== "CL"
          && loginUser.country !== "MA"
          && loginUser.country !== "JP"
          && loginUser.country !== "KZ"
          && loginUser.country !== "TW"
          && loginUser.country !== "MX" && (
          <p>Payouts are currently available in EUR.</p>
        )}
      </div>

      <Formik onSubmit={handleFormSubmit} initialValues={initialValuesCanada}>
        {({ setFieldValue }) => (
          <Form className="form-group">
            <FormGroup>
              {loginUser.country === "AU" && (
                <>
                  <Row>
                    <Col xs="12" md="10">
                      <label htmlFor="bsb_code" className="form-label">
                        BSB Code
                      </label>
                      <Field
                        id="bsb_code"
                        name="bsb_code"
                        className="form-control"
                        component={RenderTextField}
                        disabled={loginUser.needsStripeOnboarding}
                      />
                    </Col>
                    <Col xs="12" md="10">
                      <label htmlFor="account_number" className="form-label">
                        Account Number
                      </label>
                      <Field
                        id="account_number"
                        name="account_number"
                        className="form-control"
                        component={RenderTextField}
                        disabled={loginUser.needsStripeOnboarding}
                      />
                    </Col>
                  </Row>
                  {audAccountSubmitError && (
                    <p className="required">{audAccountSubmitError}</p>
                  )}
                </>
              )}
              {loginUser.country === "CA" && (
                <>
                  <Row>
                    <Col xs="12" md="10">
                      <label htmlFor="transit_number" className="form-label">
                        Transit Number
                      </label>
                      <Field
                        id="transit_number"
                        name="transit_number"
                        className="form-control"
                        component={RenderTextField}
                        disabled={loginUser.needsStripeOnboarding}
                      />
                    </Col>
                    <Col xs="12" md="10">
                      <label
                        htmlFor="institution_number"
                        className="form-label"
                      >
                        Institution Number
                      </label>
                      <Field
                        id="institution_number"
                        name="institution_number"
                        className="form-control"
                        component={RenderTextField}
                        disabled={loginUser.needsStripeOnboarding}
                      />
                    </Col>
                    <Col xs="12" md="10">
                      <label htmlFor="account_number" className="form-label">
                        Account Number
                      </label>
                      <Field
                        id="account_number"
                        name="account_number"
                        className="form-control"
                        component={RenderTextField}
                        disabled={loginUser.needsStripeOnboarding}
                      />
                    </Col>
                  </Row>
                  {cadAccountSubmitError && (
                    <p className="required">{cadAccountSubmitError}</p>
                  )}
                </>
              )}
              {loginUser.country === "HK" && (
                <>
                  <Row>
                    <Col xs="12" md="10">
                      <label htmlFor="clearing_code" className="form-label">
                        Clearing Code
                      </label>
                      <Field
                        id="clearing_code"
                        name="clearing_code"
                        className="form-control"
                        component={RenderTextField}
                        disabled={loginUser.needsStripeOnboarding}
                      />
                    </Col>
                    <Col xs="12" md="10">
                      <label htmlFor="branch_code" className="form-label">
                        Branch Code
                      </label>
                      <Field
                        id="branch_code"
                        name="branch_code"
                        className="form-control"
                        component={RenderTextField}
                        disabled={loginUser.needsStripeOnboarding}
                      />
                    </Col>
                    <Col xs="12" md="10">
                      <label htmlFor="account_number" className="form-label">
                        Account Number
                      </label>
                      <Field
                        id="account_number"
                        name="account_number"
                        className="form-control"
                        component={RenderTextField}
                        disabled={loginUser.needsStripeOnboarding}
                      />
                    </Col>
                  </Row>
                  {hkdAccountSubmitError && (
                    <p className="required">{hkdAccountSubmitError}</p>
                  )}
                </>
              )}
              {loginUser.country === "AE" && (
                <>
                  <Row>
                    <Col xs="12" md="10">
                      <label htmlFor="account_number" className="form-label">
                        IBAN
                      </label>
                      <Field
                        id="account_number"
                        name="account_number"
                        className="form-control"
                        component={RenderTextField}
                        disabled={loginUser.needsStripeOnboarding}
                      />
                    </Col>
                  </Row>
                  {aedAccountSubmitError && (
                    <p className="required">{aedAccountSubmitError}</p>
                  )}
                </>
              )}
              {loginUser.country === "IL" && (
                <>
                  <Row>
                    <Col xs="12" md="10">
                      <label htmlFor="account_number" className="form-label">
                        IBAN
                      </label>
                      <Field
                        id="account_number"
                        name="account_number"
                        className="form-control"
                        component={RenderTextField}
                        disabled={loginUser.needsStripeOnboarding}
                      />
                    </Col>
                  </Row>
                  {ilsAccountSubmitError && (
                    <p className="required">{ilsAccountSubmitError}</p>
                  )}
                </>
              )}
              {loginUser.country === "GB" && (
                <>
                  <Row>
                    <Col xs="12" md="10">
                      <label htmlFor="sort_code" className="form-label">
                        Sort Code
                      </label>
                      <Field
                        id="sort_code"
                        name="sort_code"
                        className="form-control"
                        component={RenderTextField}
                        disabled={loginUser.needsStripeOnboarding}
                      />
                    </Col>
                    <Col xs="12" md="10">
                      <label htmlFor="account_number" className="form-label">
                        Account Number
                      </label>
                      <Field
                        id="account_number"
                        name="account_number"
                        className="form-control"
                        component={RenderTextField}
                        disabled={loginUser.needsStripeOnboarding}
                      />
                    </Col>
                  </Row>
                  {gbpAccountSubmitError && (
                    <p className="required">{gbpAccountSubmitError}</p>
                  )}
                </>
              )}
              {loginUser.country === "NZ" && (
                <>
                  <Row>
                    <Col xs="12" md="10">
                      <label htmlFor="account_number" className="form-label">
                        Account Number
                      </label>
                      <Field
                        id="account_number"
                        name="account_number"
                        className="form-control"
                        component={RenderTextField}
                        disabled={loginUser.needsStripeOnboarding}
                      />
                    </Col>
                  </Row>
                  {nzdAccountSubmitError && (
                    <p className="required">{nzdAccountSubmitError}</p>
                  )}
                </>
              )}
              {loginUser.country === "MX" && (
                <>
                  <Row>
                    <Col xs="12" md="10">
                      <label htmlFor="account_number" className="form-label">
                        CLABE Number
                      </label>
                      <Field
                        id="account_number"
                        name="account_number"
                        className="form-control"
                        component={RenderTextField}
                        disabled={loginUser.needsStripeOnboarding}
                      />
                    </Col>
                  </Row>
                  {mxnAccountSubmitError && (
                    <p className="required">{mxnAccountSubmitError}</p>
                  )}
                </>
              )}
              {loginUser.country === "IN" && (
                <>
                  <Row>
                    <Col xs="12" md="10">
                      <label htmlFor="ifsc_code" className="form-label">
                        IFSC Code
                      </label>
                      <Field
                        id="ifsc_code"
                        name="ifsc_code"
                        className="form-control"
                        component={RenderTextField}
                        disabled={loginUser.needsStripeOnboarding}
                      />
                    </Col>
                    <Col xs="12" md="10">
                      <label htmlFor="account_number" className="form-label">
                        Account Number
                      </label>
                      <Field
                        id="account_number"
                        name="account_number"
                        className="form-control"
                        component={RenderTextField}
                        disabled={loginUser.needsStripeOnboarding}
                      />
                    </Col>
                  </Row>
                  {inrAccountSubmitError && (
                    <p className="required">{inrAccountSubmitError}</p>
                  )}
                </>
              )}
              {loginUser.country === "PH" && (
                <>
                  <Row>
                    <Col xs="12" md="10">
                      <label htmlFor="swift_bic" className="form-label">
                        SWIFT / BIC Code
                      </label>
                      <Field
                        id="swift_bic"
                        name="swift_bic"
                        className="form-control"
                        component={RenderTextField}
                        disabled={loginUser.needsStripeOnboarding}
                      />
                    </Col>
                    <Col xs="12" md="10">
                      <label htmlFor="account_number" className="form-label">
                        Account Number
                      </label>
                      <Field
                        id="account_number"
                        name="account_number"
                        className="form-control"
                        component={RenderTextField}
                        disabled={loginUser.needsStripeOnboarding}
                      />
                    </Col>
                  </Row>
                  {phpAccountSubmitError && (
                    <p className="required">{phpAccountSubmitError}</p>
                  )}
                </>
              )}
              {loginUser.country === "NG" && (
                <>
                  <Row>
                    <Col xs="12" md="10">
                      <label htmlFor="swift_bic" className="form-label">
                        SWIFT / BIC Code
                      </label>
                      <Field
                        id="swift_bic"
                        name="swift_bic"
                        className="form-control"
                        component={RenderTextField}
                        disabled={loginUser.needsStripeOnboarding}
                      />
                    </Col>
                    <Col xs="12" md="10">
                      <label htmlFor="account_number" className="form-label">
                        Account Number (NUBAN)
                      </label>
                      <Field
                        id="account_number"
                        name="account_number"
                        className="form-control"
                        component={RenderTextField}
                        disabled={loginUser.needsStripeOnboarding}
                      />
                    </Col>
                  </Row>
                  {ngnAccountSubmitError && (
                    <p className="required">{ngnAccountSubmitError}</p>
                  )}
                </>
              )}
              {loginUser.country === "TH" && (
                <>
                  <Row>
                    <Col xs="12" md="10">
                      <label htmlFor="bank_name" className="form-label">
                        Bank Name
                      </label>
                      <Field
                        id="bank_name"
                        name="bank_name"
                        className="form-control"
                        component={RenderTextField}
                        disabled={loginUser.needsStripeOnboarding}
                      />
                    </Col>
                    <Col xs="12" md="10">
                      <label htmlFor="bank_code" className="form-label">
                        Bank Code
                      </label>
                      <Field
                        id="bank_code"
                        name="bank_code"
                        className="form-control"
                        component={RenderTextField}
                        disabled={loginUser.needsStripeOnboarding}
                      />
                    </Col>
                    <Col xs="12" md="10">
                      <label htmlFor="account_number" className="form-label">
                        Account Number
                      </label>
                      <Field
                        id="account_number"
                        name="account_number"
                        className="form-control"
                        component={RenderTextField}
                        disabled={loginUser.needsStripeOnboarding}
                      />
                    </Col>
                  </Row>
                  {thbAccountSubmitError && (
                    <p className="required">{thbAccountSubmitError}</p>
                  )}
                </>
              )}
              {loginUser.country === "ZA" && (
                <>
                  <Row>
                    <Col xs="12" md="10">
                      <label htmlFor="swift_bic" className="form-label">
                        SWIFT / BIC Code
                      </label>
                      <Field
                        id="swift_bic"
                        name="swift_bic"
                        className="form-control"
                        component={RenderTextField}
                        disabled={loginUser.needsStripeOnboarding}
                      />
                    </Col>
                    <Col xs="12" md="10">
                      <label htmlFor="account_number" className="form-label">
                        Account Number
                      </label>
                      <Field
                        id="account_number"
                        name="account_number"
                        className="form-control"
                        component={RenderTextField}
                        disabled={loginUser.needsStripeOnboarding}
                      />
                    </Col>
                  </Row>
                  {zarAccountSubmitError && (
                    <p className="required">{zarAccountSubmitError}</p>
                  )}
                </>
              )}
              {loginUser.country === "GT" && (
                <>
                  <Row>
                    <Col xs="12" md="10">
                      <label htmlFor="swift_bic" className="form-label">
                        SWIFT / BIC Code
                      </label>
                      <Field
                        id="swift_bic"
                        name="swift_bic"
                        className="form-control"
                        component={RenderTextField}
                        disabled={loginUser.needsStripeOnboarding}
                      />
                    </Col>
                    <Col xs="12" md="10">
                      <label htmlFor="account_number" className="form-label">
                        IBAN
                      </label>
                      <Field
                        id="account_number"
                        name="account_number"
                        className="form-control"
                        component={RenderTextField}
                        disabled={loginUser.needsStripeOnboarding}
                      />
                    </Col>
                  </Row>
                  {gtqAccountSubmitError && (
                    <p className="required">{gtqAccountSubmitError}</p>
                  )}
                </>
              )}
              {loginUser.country === "AR" && (
                <>
                  <Row>
                    <Col xs="12" md="10">
                      <label htmlFor="account_number" className="form-label">
                        CBU
                      </label>
                      <Field
                        id="account_number"
                        name="account_number"
                        className="form-control"
                        component={RenderTextField}
                        disabled={loginUser.needsStripeOnboarding}
                      />
                    </Col>
                  </Row>
                  {arsAccountSubmitError && (
                    <p className="required">{arsAccountSubmitError}</p>
                  )}
                </>
              )}
              {loginUser.country === "CL" && (
                <>
                  <Row>
                    <Col xs="12" md="10">
                      <label htmlFor="bank_code" className="form-label">
                        Bank Code
                      </label>
                      <Field
                        id="bank_code"
                        name="bank_code"
                        className="form-control"
                        component={RenderTextField}
                        disabled={loginUser.needsStripeOnboarding}
                      />
                    </Col>
                    <Col xs="12" md="10">
                      <label htmlFor="account_number" className="form-label">
                        Account Number
                      </label>
                      <Field
                        id="account_number"
                        name="account_number"
                        className="form-control"
                        component={RenderTextField}
                        disabled={loginUser.needsStripeOnboarding}
                      />
                    </Col>
                    <Col xs="12" md="10">
                      <label htmlFor="account_number" className="form-label">
                        Account Type
                      </label>
                      <Field
                        id="bank_account_type"
                        name="bank_account_type"
                        onChange={(input) => {
                          setFieldValue("bank_account_type", input);
                        }}
                        options={BANK_ACCOUNT_TYPE_OPTIONS}
                        component={RenderSingleSelect}
                        disabled={loginUser.needsStripeOnboarding}
                      />
                    </Col>
                  </Row>
                  {clpAccountSubmitError && (
                    <p className="required">{clpAccountSubmitError}</p>
                  )}
                </>
              )}
              {loginUser.country === "MA" && (
                <>
                  <Row>
                    <Col xs="12" md="10">
                      <label htmlFor="swift_bic" className="form-label">
                        SWIFT / BIC Code
                      </label>
                      <Field
                        id="swift_bic"
                        name="swift_bic"
                        className="form-control"
                        component={RenderTextField}
                        disabled={loginUser.needsStripeOnboarding}
                      />
                    </Col>
                    <Col xs="12" md="10">
                      <label htmlFor="account_number" className="form-label">
                        IBAN
                      </label>
                      <Field
                        id="account_number"
                        name="account_number"
                        className="form-control"
                        component={RenderTextField}
                        disabled={loginUser.needsStripeOnboarding}
                      />
                    </Col>
                  </Row>
                  {madAccountSubmitError && (
                    <p className="required">{madAccountSubmitError}</p>
                  )}
                </>
              )}
              {loginUser.country === "JP" && (
                <>
                  <Row>
                    <Col xs="12" md="10">
                      <label
                        htmlFor="account_holder_name"
                        className="form-label"
                      >
                        Account Owner Name (Romaji)
                      </label>
                      <Field
                        id="account_holder_name"
                        name="account_holder_name"
                        className="form-control"
                        component={RenderTextField}
                        disabled={loginUser.needsStripeOnboarding}
                      />
                    </Col>
                    <Col xs="12" md="10">
                      <label htmlFor="bank_name" className="form-label">
                        Bank Name
                      </label>
                      <Field
                        id="bank_name"
                        name="bank_name"
                        className="form-control"
                        component={RenderTextField}
                        disabled={loginUser.needsStripeOnboarding}
                      />
                    </Col>
                    <Col xs="12" md="10">
                      <label htmlFor="bank_code" className="form-label">
                        Bank Code
                      </label>
                      <Field
                        id="bank_code"
                        name="bank_code"
                        className="form-control"
                        component={RenderTextField}
                        disabled={loginUser.needsStripeOnboarding}
                      />
                    </Col>
                    <Col xs="12" md="10">
                      <label htmlFor="branch_code" className="form-label">
                        Branch Code
                      </label>
                      <Field
                        id="branch_code"
                        name="branch_code"
                        className="form-control"
                        component={RenderTextField}
                        disabled={loginUser.needsStripeOnboarding}
                      />
                    </Col>
                    <Col xs="12" md="10">
                      <label htmlFor="account_number" className="form-label">
                        Account Number
                      </label>
                      <Field
                        id="account_number"
                        name="account_number"
                        className="form-control"
                        component={RenderTextField}
                        disabled={loginUser.needsStripeOnboarding}
                      />
                    </Col>
                  </Row>
                  {jpyAccountSubmitError && (
                    <p className="required">{jpyAccountSubmitError}</p>
                  )}
                </>
              )}
              {loginUser.country === "KZ" && (
                <>
                  <Row>
                    <Col xs="12" md="10">
                      <label htmlFor="swift_bic" className="form-label">
                        SWIFT / BIC Code
                      </label>
                      <Field
                        id="swift_bic"
                        name="swift_bic"
                        className="form-control"
                        component={RenderTextField}
                        disabled={loginUser.needsStripeOnboarding}
                      />
                    </Col>
                    <Col xs="12" md="10">
                      <label htmlFor="account_number" className="form-label">
                        IBAN
                      </label>
                      <Field
                        id="account_number"
                        name="account_number"
                        className="form-control"
                        component={RenderTextField}
                        disabled={loginUser.needsStripeOnboarding}
                      />
                    </Col>
                  </Row>
                  {kztAccountSubmitError && (
                    <p className="required">{kztAccountSubmitError}</p>
                  )}
                </>
              )}
              {loginUser.country === "TW" && (
                <>
                  <Row>
                    <Col xs="12" md="10">
                      <label htmlFor="swift_bic" className="form-label">
                        SWIFT / BIC Code
                      </label>
                      <Field
                        id="swift_bic"
                        name="swift_bic"
                        className="form-control"
                        component={RenderTextField}
                        disabled={loginUser.needsStripeOnboarding}
                      />
                    </Col>
                    <Col xs="12" md="10">
                      <label htmlFor="account_number" className="form-label">
                        Account Number
                      </label>
                      <Field
                        id="account_number"
                        name="account_number"
                        className="form-control"
                        component={RenderTextField}
                        disabled={loginUser.needsStripeOnboarding}
                      />
                    </Col>
                  </Row>
                  {twdAccountSubmitError && (
                    <p className="required">{twdAccountSubmitError}</p>
                  )}
                </>
              )}
              {loginUser.country !== "GB"
                && loginUser.country !== "NZ"
                && loginUser.country !== "AE"
                && loginUser.country !== "HK"
                && loginUser.country !== "CA"
                && loginUser.country !== "AU"
                && loginUser.country !== "US"
                && loginUser.country !== "IL"
                && loginUser.country !== "ZA"
                && loginUser.country !== "TH"
                && loginUser.country !== "PH"
                && loginUser.country !== "IN"
                && loginUser.country !== "NG"
                && loginUser.country !== "CK"
                && loginUser.country !== "AR"
                && loginUser.country !== "GT"
                && loginUser.country !== "MA"
                && loginUser.country !== "CL"
                && loginUser.country !== "JP"
                && loginUser.country !== "KZ"
                && loginUser.country !== "TW"
                && loginUser.country !== "MX" && (
                <>
                  <Row>
                    <Col xs="12" md="10">
                      <label htmlFor="iban" className="form-label">
                          IBAN
                        {ibanSubmitError && (
                          <span className="required ml-2">
                            {ibanSubmitError}
                          </span>
                        )}
                      </label>
                      <IbanElement
                        options={{
                          supportedCountries: ["SEPA"],
                          placeholderCountry: loginUser.country,
                          style: {
                            base: {
                              fontFamily:
                                  "HKGrotesk-Regular, Arial, sans-serif",
                            },
                          },
                          disabled: loginUser.needsStripeOnboarding,
                          classes: { base: "form-control stripe-iban-input" },
                        }}
                      />
                    </Col>
                  </Row>
                </>
              )}
              <button
                className="btn btn-default btn-primary mt-2"
                type="submit"
                disabled={
                  !stripe
                  || !elements
                  || loginUser.needsStripeOnboarding
                  || loading
                }
              >
                {loading ? (
                  <Spinner size="sm" />
                ) : (
                  <>
                    {isReplaceAccountForm
                      ? "Update Linked Account"
                      : "Add Account and Save"}
                  </>
                )}
              </button>
            </FormGroup>
          </Form>
        )}
      </Formik>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    loginUser: state.login.loginUser,
    loading: state.settings.loading,
  };
};
export default connect(mapStateToProps, {
  addExternalAccount,
})(InternationalAccountForm);
